import type {
  CanonicalTreeRuntimeAuthorityConvergenceBlocker,
  CanonicalTreeRuntimeAuthorityConvergenceResult,
} from './CanonicalTreeActiveRuntimeAuthorityConvergenceContract';

export type CanonicalTreeRuntimeContainmentStatus =
  | 'READY'
  | 'DEGRADED'
  | 'UNSAFE';

export type CanonicalTreeRuntimeContainmentAction =
  | 'RENDER_CANONICAL'
  | 'RENDER_WITH_WARNING'
  | 'SUPPRESS_UNCERTIFIED_GEOMETRY';

export interface CanonicalTreeRuntimeContainmentResult {
  readonly status:
    CanonicalTreeRuntimeContainmentStatus;
  readonly action:
    CanonicalTreeRuntimeContainmentAction;
  readonly blockers:
    readonly CanonicalTreeRuntimeAuthorityConvergenceBlocker[];
  readonly preservesRoster: true;
  readonly preservesProfiles: true;
  readonly preservesRepairControls: true;
  readonly permitsBlankTreeRoute: false;
  readonly permitsFamilyRecordMutation: false;
  readonly mutatesCanonicalGraph: false;
}

const unsafeBlockers =
  new Set<
    CanonicalTreeRuntimeAuthorityConvergenceBlocker
  >([
    'VIEWER_PROJECTION_UNAVAILABLE',
    'VISIBLE_MEMBER_PROJECTION_MISSING',
    'VISIBLE_MEMBER_GEOMETRY_ROLE_MISSING',
    'CONNECTOR_EDGE_MISSING',
    'VISIBLE_MEMBER_SET_MISMATCH',
  ]);

export const resolveCanonicalTreeRuntimeConvergenceContainment = (
  convergence:
    CanonicalTreeRuntimeAuthorityConvergenceResult,
): CanonicalTreeRuntimeContainmentResult => {
  const blockers =
    Object.freeze([
      ...convergence.blockers,
    ]);

  if (convergence.status === 'READY') {
    return Object.freeze({
      status: 'READY',
      action: 'RENDER_CANONICAL',
      blockers,
      preservesRoster: true,
      preservesProfiles: true,
      preservesRepairControls: true,
      permitsBlankTreeRoute: false,
      permitsFamilyRecordMutation: false,
      mutatesCanonicalGraph: false,
    });
  }

  const unsafe =
    blockers.some(
      blocker =>
        unsafeBlockers.has(blocker),
    );

  return Object.freeze({
    status:
      unsafe
        ? 'UNSAFE'
        : 'DEGRADED',
    action:
      unsafe
        ? 'SUPPRESS_UNCERTIFIED_GEOMETRY'
        : 'RENDER_WITH_WARNING',
    blockers,
    preservesRoster: true,
    preservesProfiles: true,
    preservesRepairControls: true,
    permitsBlankTreeRoute: false,
    permitsFamilyRecordMutation: false,
    mutatesCanonicalGraph: false,
  });
};
